import { DataGrid } from "devextreme-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useRecoilCallback, useRecoilValue } from "recoil";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { useLocation, useNavigate } from "react-router-dom";

import { AppName, ScheduleCustomPermissionStatusesEnum, CUSTOM_PERMISSION_BANNER_DATA } from "../../../constants";
import { convertFormatDate, convertUTCTimeToLocal, formatUsersCount } from "../../../util/Utils";
import { SnackbarMessage, useSnackbar } from "../../../hooks";
import { IsFileManagementMicroserviceEnabled, REACT_APP_USE_FOLDER_PERMISSIONS_PAGE } from "../../../envVariables";
import { FormatDate } from "../../../api-client-nswag/taxportal-client";
import { useLegalEntitiesActions } from "../../../recoil/actions/legalEntities.actions";
import { buttonDisabledSelector, entitiesSelector, folderPermissionSelector, folderPermissionSnapshotSelector, isLegalEntityDirtySelector, isScheduleActionDisabledSelector, snapshotEntitiesSelector, usersPerSiteSelector } from "../../../recoil/selectors/legalEntities.selectors";
import { useUsersActions } from "../../../recoil/actions/users.actions";
import { isUserListDirtySelector, usersInEntitySelector } from "../../../recoil/selectors/users.selectors";
import { createSitesClientApi, customPermissionClientApi, taxPortalClientApi } from "../../../api-client-nswag/taxportal-client-runtime";
import { Entity, UserPerSite, CustomPermissionPayload, EntityPermission, UpdateScheduleRequest, ScheduledHqSiteCountByDateDto, FolderNode, FolderTree } from "../../../api-client-nswag/custompermission-client";
import { Descriptions } from "../../../api-client-nswag/taxportal-client";
import ActionIconButton from "../../../cui/commonComponents/ActionIconButton";
import { useFolderTreeActions } from "../../../recoil/actions/folderTree.actions";
import { folderTreeSelector, isEntityFolderTreeUsersDirtySelector, isSiteFolderPermissionDirtySelector } from "../../../recoil/selectors/folderTree.selectors";
import { BulkEntityUsersMap } from "../BulkUserAssignmentManager";
import { exportManageCustomPermissionsToExcel } from "../exportManageCustomPermissionsToExcel";
import { ManageCustomPermissionsLandingContextValue } from "../ManageCustomPermissionsLanding.types";
import { applyOverflowTooltipOnCellPrepared, renderTruncatedTextCell } from "../manageCustomPermissionsGridHelpers";


type DatagridRefType = DataGrid;

export const legalEntitiesActionOptions = [
  {
    label: "Edit Users",
    value: "edit-users",
  },
  {
    label: "Folder Permissions",
    value: "folder-permissions",
  },
];


export const useManageCustomPermissionsLandingController = (): ManageCustomPermissionsLandingContextValue => {
  const navigate = useNavigate();
  const location = useLocation();
  const navState = (location.state as { preserveFlow?: boolean; openScheduleFromFolderPage?: boolean } | null) ?? null;
  let siteID = parseInt(sessionStorage.getItem("siteId")!);
  const dataGridRef = useRef<DatagridRefType>(null);
  const { showSnackbar } = useSnackbar();
  const { loadFromApi, updateIsButtonDisabled, updateFolderPermission, updateEntityUserList, updateEntityUserListFolderPermission, updateEntityFolderTree, updateSiteFolderPermission, revalidateEntityIsRestricted, revalidateAllEntitiesIsRestricted, resetEntitiesToSnapshot, resetEntitiesState, loadFromRefreshFolderPermissions } = useLegalEntitiesActions();
  const { loadUsersInEntity, loadUsersPerSite } = useUsersActions();
  const { loadFolderTree } = useFolderTreeActions()
  const isDirty = useRecoilValue(isLegalEntityDirtySelector);
  const entities = useRecoilValue(entitiesSelector)
  const snapshotEntities = useRecoilValue(snapshotEntitiesSelector)
  const users = useRecoilValue(usersPerSiteSelector)
  const isButtonDisabled = useRecoilValue(buttonDisabledSelector) ?? false
  const isScheduleActionDisabled = useRecoilValue(isScheduleActionDisabledSelector)
  const folderPermissions = useRecoilValue(folderPermissionSelector)
  const folderPermissionSnapshot = useRecoilValue(folderPermissionSnapshotSelector)
  const isDirtyRef = useRef(isDirty);
  const [selectedLegalEntities, setSelectedLegalEntities] =
    useState<Entity>();
  const [selectedEntityIds, setSelectedEntityIds] = useState<Array<string | number>>([]);
  const [bulkSelectedEntities, setBulkSelectedEntities] = useState<Entity[]>([]);
  const [showPublicationNotice, setShowPublicationNotice] = useState(true);
  const [showUserModel, setShowUserModel] = useState(false);
  const [showBulkUserModel, setShowBulkUserModel] = useState(false);
  const [bulkEntityUsersMap, setBulkEntityUsersMap] = useState<BulkEntityUsersMap>({});
  const [bulkEntityUsersSnapshot, setBulkEntityUsersSnapshot] = useState<BulkEntityUsersMap>({});
  const [isloading, setIsLoading] = useState(false);
  const [scheduleDate, setScheduleDate] = useState(null);
  const [scheduleDetails, setScheduleDetails] = useState({ status: null as string | null, scheduleDate: '' });
  const [showFolderPermissionModel, setShowFolderPermissionModel] = useState(false);
  const [showFolderPermissionView, setShowFolderPermissionView] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [showScheduleProcessModal, setShowScheduleProcessModal] = useState(false);
  const [showCancelProcessModel, setShowCancelProcessModel] = useState(false);
  const [isModalFromSaveButton, setIsModalFromSaveButton] = useState(false);
  const [isSiteLevelFolderPermission, setIsSiteLevelFolderPermissoin] = useState(false);
  const [isRefreshingFolderPermissions, setIsRefreshingFolderPermissions] = useState(false);
  const [scheduledCountsDisabledDates, setScheduledCountsDisabledDates] = useState<string[]>([]);
  const scheduledCountsDisabledDatesSet = useMemo(() => new Set(scheduledCountsDisabledDates), [scheduledCountsDisabledDates]);
  const [maxUniqueHqSiteCount, setMaxUniqueHqSiteCount] = useState<number | undefined>(undefined);
  const usersInEntitySelectorValues = useRecoilValue(usersInEntitySelector)
  const folderTreeSelectorValues = useRecoilValue(folderTreeSelector)
  const isFolderTreeDirty = useRecoilValue(isEntityFolderTreeUsersDirtySelector(selectedLegalEntities?.entityId || 0))
  const isSiteFolderPermissionDirty = useRecoilValue(isSiteFolderPermissionDirtySelector);
  const isUserlistDirty = useRecoilValue(isUserListDirtySelector);
  const [isCsFlow, setIsCsFlow] = useState<boolean>(false);
  const shouldUseStandaloneFolderPermissionsPage =
    String(REACT_APP_USE_FOLDER_PERMISSIONS_PAGE ?? "false").toLowerCase() === "true";


  const getLatestRecoilForFolderPermissions = useRecoilCallback(
    ({ snapshot }) =>
      async () => {
        const [latestEntities, latestUsersPerSite, latestFolderPermission] = await Promise.all([
          snapshot.getPromise(entitiesSelector) as Promise<Entity[]>,
          snapshot.getPromise(usersPerSiteSelector) as Promise<UserPerSite[]>,
          snapshot.getPromise(folderPermissionSelector) as Promise<any[]>,
        ]);

        return {
          latestEntities: Array.isArray(latestEntities) ? latestEntities : [],
          latestUsersPerSite: Array.isArray(latestUsersPerSite) ? latestUsersPerSite : [],
          latestFolderPermission: Array.isArray(latestFolderPermission) ? latestFolderPermission : [],
        };
      },
    []
  );

  const refreshEntitiesGrid = () => {
    const instance: any = (dataGridRef.current as any)?.instance;
    instance?.refresh?.();
    instance?.repaint?.();
  };


  const formatScheduledDateForDisplay = (value: any): string => {
    const raw = String(value ?? "").trim();
    if (!raw) return "";

    const isoDate = raw.length >= 10 ? raw.substring(0, 10) : "";
    const parts = isoDate.split("-");
    if (parts.length === 3 && parts[0].length === 4) {
      const [yyyy, mm, dd] = parts;
      if (yyyy && mm && dd) return `${mm}/${dd}/${yyyy}`;
    }

    return raw;
  };


  const formatScheduleDateForApi = (value: any): string | undefined => {
    if (!value) return undefined;

    if (typeof value === "string") {
      const raw = value.trim();
      const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) return `${m[1]}-${m[2]}-${m[3]}`;
    }

    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return undefined;

    const isLocalMidnight =
      d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0 && d.getMilliseconds() === 0;

    const year = isLocalMidnight ? d.getFullYear() : d.getUTCFullYear();
    const month = String((isLocalMidnight ? d.getMonth() : d.getUTCMonth()) + 1).padStart(2, "0");
    const day = String(isLocalMidnight ? d.getDate() : d.getUTCDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  type PublicationLifecycleStatus =
    | "Not Restricted"
    | "Modified"
    | "Saved"
    | "Scheduled"
    | "Processing"
    | "Complete";

  const normalizeStatusKey = (raw?: string | null) => (raw ?? "").trim().toUpperCase();

  const areStringArraysEqual = (a?: string[], b?: string[]) => {
    const aa = Array.isArray(a) ? a : [];
    const bb = Array.isArray(b) ? b : [];
    if (aa.length !== bb.length) return false;
    const as = [...aa].map(String).sort();
    const bs = [...bb].map(String).sort();
    for (let i = 0; i < as.length; i++) {
      if (as[i] !== bs[i]) return false;
    }
    return true;
  };

  const computeChangedFolderIds = (
    currentTree: FolderTree,
    snapshotTree: FolderTree,
    baselineUsers: string[]
  ): number[] => {
    const getEffectiveUsers = (node: FolderNode): string[] =>
      node.isRestricted !== true && !node.users?.length
        ? baselineUsers
        : node.users ?? [];

    const indexTree = (tree: FolderTree) => {
      const nodes = new Map<number, FolderNode>();
      const visit = (node: FolderNode) => {
        nodes.set(node.folderId, node);
        (node.children ?? []).forEach(visit);
      };
      tree.forEach(visit);
      return nodes;
    };

    const currentNodes = indexTree(currentTree);
    const snapshotNodes = indexTree(snapshotTree);
    const changed = new Set<number>();

    currentNodes.forEach((node, folderId) => {
      const original = snapshotNodes.get(folderId);
      if (!original ||
        (node.isRestricted === true) !== (original.isRestricted === true) ||
        !areStringArraysEqual(getEffectiveUsers(node), getEffectiveUsers(original))) {
        changed.add(folderId);
      }
    });
    snapshotNodes.forEach((_node, folderId) => {
      if (!currentNodes.has(folderId)) changed.add(folderId);
    });

    const includeAncestors = (node: FolderNode): boolean => {
      const childChanges = (node.children ?? []).map(includeAncestors);
      if (childChanges.some(Boolean)) changed.add(node.folderId);
      return changed.has(node.folderId);
    };
    currentTree.forEach(includeAncestors);
    snapshotTree.forEach(includeAncestors);

    return Array.from(changed).sort((a, b) => a - b);
  };

  const folderPermissionChangedCount = useMemo(
    () => computeChangedFolderIds(
      folderPermissions ?? [], folderPermissionSnapshot ?? [], users.map(user => String(user.userId))
    ).length,
    [folderPermissions, folderPermissionSnapshot, users]
  );

  const duplicateEntityFolderNames = useMemo((): string[] => {
    const entityChildren = folderPermissions?.[0]?.children;
    if (!Array.isArray(entityChildren) || entityChildren.length === 0) return [];

    const countById: Record<number, number> = {};
    for (const folder of entityChildren) {
      const id = folder.entityId;
      if (!id || id === 0) continue;
      countById[id] = (countById[id] ?? 0) + 1;
    }

    const duplicateIds = new Set<number>(
      Object.entries(countById)
        .filter(([, count]) => count > 1)
        .map(([id]) => Number(id))
    );

    if (duplicateIds.size === 0) return [];

    return Array.from(duplicateIds).map((id) => {
      const entity = entities.find((e: Entity) => e.entityId === id);
      return entity?.entityName ?? `Entity ${id}`;
    });
  }, [folderPermissions, entities]);

  const isEntityDirty = (entity: Entity) => {
    const snap = (snapshotEntities ?? []).find((e: Entity) => e?.entityId === entity.entityId);
    if (!snap) return false;

    if (!!entity.isRestricted !== !!snap.isRestricted) return true;
    const baselineUsers = users.map(user => String(user.userId));
    const entityUsers = !entity.isRestricted && !entity.userList?.length ? baselineUsers : entity.userList;
    const snapshotUsers = !snap.isRestricted && !snap.userList?.length ? baselineUsers : snap.userList;
    if (!areStringArraysEqual(entityUsers, snapshotUsers)) return true;

    const currTree = Array.isArray(entity.folderTree) ? entity.folderTree : [];
    const snapTree = Array.isArray(snap.folderTree) ? snap.folderTree : [];
    return computeChangedFolderIds(currTree, snapTree, baselineUsers).length > 0;
  };

  const resolveLifecycleStatus = (entity: Entity): PublicationLifecycleStatus => {
    const snapshot = (snapshotEntities ?? []).find(
      (e: Entity) => e?.entityId === entity.entityId
    );
    const key = normalizeStatusKey(entity.publishStatus);
    const hasPublicationHistory =
      snapshot?.isRestricted === true ||
      entity.isChanged === true ||
      !!entity.scheduledPublishDate ||
      !!entity.publishedDate ||
      ["IN PROGRESS", "PROCESSING", "SCHEDULED", "SAVED", "NOT SCHEDULED", "CHANGED", "COMPLETE", "COMPLETED"].includes(key);

    if (entity.isRestricted === false && !hasPublicationHistory) return "Not Restricted";
    if (isEntityDirty(entity)) return "Modified";

    if (key === "IN PROGRESS" || key === "PROCESSING") return "Processing";

    const hasPendingChanges = key === "CHANGED" || entity.isChanged === true;

    if (!hasPendingChanges && entity.publishedDate) return "Complete";

    if (key === "COMPLETED") return "Complete";
    if (key === "SCHEDULED") return "Scheduled";
    if (key === "SAVED" || key === "NOT SCHEDULED") return "Saved";

    if (hasPendingChanges) return "Saved";

    return entity.isRestricted || hasPublicationHistory ? "Saved" : "Not Restricted";
  };

  const renderStatusChip = (status: PublicationLifecycleStatus) => {
    const variant =
      status === "Complete" ? "success" :
        status === "Modified" ? "error" :
          status === "Saved" ? "partial" :
            status === "Scheduled" ? "scheduled" :
              status === "Processing" ? "inprogress" :
                status === "Not Restricted" ? "default" :
                  "default";

    const tooltipMessage =
      status === "Not Restricted" ? "No restrictions are applied." :
        status === "Modified" ? "Changes have been made but not saved." :
          status === "Saved" ? "Changes are saved but not scheduled." :
            status === "Scheduled" ? "Changes are scheduled for publication." :
              status === "Processing" ? "Publication is currently being processed." :
                "Publication is complete.";

    return <span className={`status-pill ${variant}`} title={tooltipMessage}>{status}</span>;
  };

  const cellRenderStatus = (e: any) => {
    const status = resolveLifecycleStatus(e.data as Entity);
    return renderStatusChip(status);
  };

  const cellRenderScheduledDate = (e: any) => {
    const entity = e.data as Entity;
    const raw = entity.scheduledPublishDate;
    if (!raw) return null;
    return <span className="cell-text">{convertUTCTimeToLocal(raw, FormatDate.DD)}</span>;
  };

  const cellRenderPublishedDate = (e: any) => {
    const entity = e.data as Entity;
    const raw = entity.publishedDate;
    if (!raw) return null;
    return <span className="cell-text">{convertUTCTimeToLocal(raw, FormatDate.DD)}</span>;
  };

  const handleVerifyChanges = async () => {
    try {
      const updateRequest: UpdateScheduleRequest = {
        siteId: siteID,
        status: "VERIFIED"
      };
      await customPermissionClientApi.updateLegalEntityPermissionsSchedule(updateRequest);
      await fetchLegalEntities();
    } catch (error) {
      showSnackbar({ message: SnackbarMessage.FAILED_MESSAGE, severity: "error" });
    }
  };

  const customPermissionBannerData = {
    [ScheduleCustomPermissionStatusesEnum.NotScheduled]: {
      ...CUSTOM_PERMISSION_BANNER_DATA["NOT SCHEDULED"],
      ...(!isScheduleActionDisabled && {
        actionTextAction: () => {
          openScheduleProcessModal(false);
        }
      })
    },
    [ScheduleCustomPermissionStatusesEnum.Scheduled]: {
      ...CUSTOM_PERMISSION_BANNER_DATA["SCHEDULED"](scheduleDetails?.scheduleDate),
      actionTextAction: () => {
        setShowCancelProcessModel(true)
      }
    },
    [ScheduleCustomPermissionStatusesEnum.VerificationInProgress]: {
      ...CUSTOM_PERMISSION_BANNER_DATA["VERIFICATION IN PROGRESS"],
      actionTextAction: () => {
        handleVerifyChanges()
      }
    },
    [ScheduleCustomPermissionStatusesEnum.InProgress]: CUSTOM_PERMISSION_BANNER_DATA["IN PROGRESS"],
    [ScheduleCustomPermissionStatusesEnum.Verified]: {
      ...CUSTOM_PERMISSION_BANNER_DATA["VERIFIED"],
      actionTextAction: () => {
        setShowPublicationNotice(false)
      }
    },
    [ScheduleCustomPermissionStatusesEnum.Cancelled]: {
      ...CUSTOM_PERMISSION_BANNER_DATA["CANCELLED"],
      ...(!isScheduleActionDisabled && {
        actionTextAction: () => {
          openScheduleProcessModal(false);
        }
      })
    }
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        setShowUnsavedChangesModal(false);
      }

      if (document.visibilityState === "visible") {
        setShowUnsavedChangesModal(false);
        refreshScheduleDetails();
      }
    };

    const handleBlur = (e: any) => {
      if (document.visibilityState === "hidden") return;
      if (isDirtyRef.current) {
        setShowUnsavedChangesModal(true);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleBlur);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleBlur);
    };
  }, []);

  useEffect(() => {
    if (navState?.preserveFlow) {
      if (navState.openScheduleFromFolderPage) {
        openScheduleProcessModal(true);
      }

      navigate(location.pathname, { replace: true, state: null });
      return;
    }

    clearEntitySelection();
    fetchLegalEntities();
  }, []);

  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  useEffect(() => {
    loadFolderTree(folderPermissions);
  }, [folderPermissions, loadFolderTree]);

  const refreshScheduleDetails = async () => {
    try {
      const schedule = await customPermissionClientApi.getLegalEntityPermissionsSchedule(siteID);
      const status = schedule?.status?.trim().toUpperCase() || null;
      setScheduleDetails({
        status,
        scheduleDate: schedule?.scheduledDate ? formatScheduledDateForDisplay(schedule.scheduledDate) : '',
      });
      updateIsButtonDisabled({ status });
      if (status === ScheduleCustomPermissionStatusesEnum.VerificationInProgress
        || status === ScheduleCustomPermissionStatusesEnum.InProgress) {
        setShowPublicationNotice(true);
      }
    } catch {
      showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
    }
  };

  const fetchLegalEntities = async (): Promise<{
    entities: Entity[];
    usersPerSite: UserPerSite[];
    folderTreeData: any[];
  } | undefined> => {
    setIsLoading(true);
    try {
      clearEntitySelection();
      resetEntitiesState()
      const isCs = await fetchGetAppName()
      const [entitiesRes, folderRes, scheduleRes] = await Promise.allSettled([
        customPermissionClientApi.getCustomPermissionManagementpage(siteID),
        customPermissionClientApi.getFolderTree(siteID),
        customPermissionClientApi.getLegalEntityPermissionsSchedule(siteID),
      ]);

      if (scheduleRes.status === "fulfilled" && scheduleRes.value) {
        const scheduleData = scheduleRes.value;
        const apiStatus = scheduleData.status;
        let statusEnum: string | null = null;

        if (apiStatus && apiStatus.trim() !== '') {
          statusEnum = apiStatus.toUpperCase().trim();
        }

        setScheduleDetails((prev) => ({
          ...prev,
          status: statusEnum,
          scheduleDate: scheduleData.scheduledDate ? formatScheduledDateForDisplay(scheduleData.scheduledDate) : '',
        }));
        updateIsButtonDisabled({ status: statusEnum })
      } else {
        setScheduleDetails((prev) => ({
          ...prev,
          status: null,
        }));
        updateIsButtonDisabled({ status: null })
      }

      if (entitiesRes.status === "fulfilled" && folderRes.status === "fulfilled") {
        const entities = entitiesRes.value?.entities ?? [];
        const usersPerSite = entitiesRes.value?.usersPerSite ?? [];
        const folderTreeData = folderRes.value ?? [];

        loadFromApi({ Entities: entities, UsersPerSite: usersPerSite, folderPermission: folderTreeData });

        if (folderTreeData.length > 0) {
          updateFolderPermission({ folderPermission: folderTreeData[0], usersPerSite, isCSFlow: isCs });
        }

        return { entities, usersPerSite, folderTreeData };
      } else {
        showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
      }
    } catch (error) {
      showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
    } finally {
      setIsLoading(false);
    }

    return undefined;
  };

  const fetchGetAppName = async () => {
    try {
      const appName = await taxPortalClientApi.taxPortal_GetAppName(siteID);
      const normalizedAppName = typeof appName === "string" ? JSON.parse(appName) : appName;
      const isMatch = normalizedAppName === AppName.CS;
      if (shouldUseStandaloneFolderPermissionsPage) {
        setIsCsFlow(isMatch);
        setShowFolderPermissionView(isMatch);
        return isMatch;
      }
      return false
    } catch (error) {
      showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
      return false;
    }
  }

  const refreshManageCustomPermissionsAndReopenFolderPermissions = async (ctx: {
    isSiteLevelFolderPermission: boolean;
    entityId?: number;
  }) => {
    try {
      if (isRefreshingFolderPermissions) return;
      const preservedEntities = entities.filter((entity) => {
        const snapshotEntity = snapshotEntities.find(
          (snapshot) => snapshot.entityId === entity.entityId
        );
        if (!snapshotEntity) return false;

        const currentUsers = new Set((entity.userList ?? []).map(String));
        const snapshotUsers = new Set((snapshotEntity.userList ?? []).map(String));
        const userListsDiffer =
          currentUsers.size !== snapshotUsers.size ||
          Array.from(currentUsers).some((userId) => !snapshotUsers.has(userId));

        return entity.isRestricted !== snapshotEntity.isRestricted || userListsDiffer;
      });
      setIsRefreshingFolderPermissions(true);
      await (
        String(IsFileManagementMicroserviceEnabled ?? "").toLowerCase() === "true"
          ? createSitesClientApi.iManageWorkspace_RefreshEntityImanage(siteID, true, Descriptions.TAXPORTAL_FOLDERS)
          : taxPortalClientApi.hQFolder_RefreshEntityImanage(siteID, true, Descriptions.TAXPORTAL_FOLDERS)
      );
      await fetchLegalEntities();
      const { latestEntities, latestUsersPerSite, latestFolderPermission } =
        await getLatestRecoilForFolderPermissions();

      loadFromRefreshFolderPermissions({
        Entities: latestEntities,
        preservedEntities,
        ...(ctx.isSiteLevelFolderPermission
          ? {
            folderPermission: latestFolderPermission,
          }
          : {}),
      });

      if (ctx.isSiteLevelFolderPermission) {
        loadFolderTree(latestFolderPermission);
        openFolderPermission(setShowFolderPermissionView, setShowFolderPermissionModel);
        return;
      }

      const targetEntityId = Number(ctx.entityId);
      const refreshedEntity = latestEntities.find((e) => Number(e?.entityId) === targetEntityId);
      const preservedEntity = preservedEntities.find((e) => e.entityId === targetEntityId);
      const entity = refreshedEntity && preservedEntity
        ? {
          ...refreshedEntity,
          isRestricted: preservedEntity.isRestricted,
          userList: preservedEntity.userList,
          numberOfUsers: preservedEntity.numberOfUsers,
        }
        : refreshedEntity;
      if (entity) {
        if (!entity.isRestricted) {
          const userIds = latestUsersPerSite.map((u) => String(u.userId));
          updateEntityUserList(entity.entityId, userIds);
        }

        setSelectedLegalEntities(entity);
        loadFolderTree(entity?.folderTree);
        setIsSiteLevelFolderPermissoin(false);
        return;
      }

      loadFolderTree(latestFolderPermission);
      setIsSiteLevelFolderPermissoin(true);
      openFolderPermission(setShowFolderPermissionView, setShowFolderPermissionModel)
    } catch (error) {
      showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
      openFolderPermission(setShowFolderPermissionView, setShowFolderPermissionModel)
    } finally {
      setIsRefreshingFolderPermissions(false);
    }
  };

  const clearEntitySelection = () => {
    setSelectedEntityIds([]);
    setBulkSelectedEntities([]);
  };

  const fetchUsersForLegalEntity = (entity?: Entity) => {
    if (!entity) return

    if (!entity.isRestricted) {
      loadUsersInEntity(users)
    } else {
      const entityUserIds = new Set((entity?.userList || []).map(String))
      const usersInEntity = users.filter(u => entityUserIds.has(String(u.userId)))
      const usersNotInEntity = users.filter(u => !entityUserIds.has(String(u.userId)))

      loadUsersInEntity(usersInEntity)
      loadUsersPerSite(usersNotInEntity)
    }
  }


  const getSelectedEntityKeysFromGrid = (): Array<string | number> => {
    const instance: any = (dataGridRef.current as any)?.instance;
    const keys = (instance?.getSelectedRowKeys?.() as Array<string | number> | undefined) ?? [];
    return Array.isArray(keys) ? keys : [];
  };

  const buildBulkUsersMap = (targets: Entity[]): BulkEntityUsersMap => {
    const allUserIds = (users ?? []).map(u => String(u.userId));
    const next: BulkEntityUsersMap = {};

    for (const entity of targets) {
      const entityId = Number((entity as any)?.entityId);
      if (!Number.isFinite(entityId) || entityId === 0) continue;

      const userIds = entity?.isRestricted
        ? (entity?.userList ?? []).map(String)
        : allUserIds;

      next[entityId] = Array.from(new Set(userIds));
    }

    return next;
  };

  const handleBulkSelectedEntityIdsChange = (nextSelectedIds: Array<string | number>) => {
    const normalizeIds = (keys: Array<string | number>) => {
      const ids = (keys ?? [])
        .map((k) => Number(k))
        .filter((id) => Number.isFinite(id) && id !== 0)
        .sort((a, b) => a - b);
      return ids.join(",");
    };

    const currentNorm = normalizeIds((bulkSelectedEntities ?? []).map((e: any) => e?.entityId));
    const nextNorm = normalizeIds(nextSelectedIds ?? []);
    if (currentNorm === nextNorm) return;

    setSelectedEntityIds((nextSelectedIds ?? []) as Array<string | number>);

    const ids = (nextSelectedIds ?? []).map((k) => String(k));
    const idSet = new Set(ids);

    const nextTargets = (entities ?? []).filter((e: any) => idSet.has(String(e?.entityId)));
    setBulkSelectedEntities(nextTargets);

    const allUserIds = (users ?? []).map((u) => String(u.userId));
    const baselineFor = (entity: Entity): string[] =>
      entity?.isRestricted ? (entity?.userList ?? []).map(String) : allUserIds;

    setBulkEntityUsersMap((prev) => {
      const next: BulkEntityUsersMap = {};
      for (const entity of nextTargets) {
        const entityId = Number((entity as any)?.entityId);
        if (!Number.isFinite(entityId) || entityId === 0) continue;

        const existing = prev?.[entityId];
        const value = existing ?? baselineFor(entity);
        next[entityId] = Array.from(new Set((value ?? []).map(String)));
      }
      return next;
    });

    setBulkEntityUsersSnapshot((prev) => {
      const next: BulkEntityUsersMap = {};
      for (const entity of nextTargets) {
        const entityId = Number((entity as any)?.entityId);
        if (!Number.isFinite(entityId) || entityId === 0) continue;

        const existing = prev?.[entityId];
        const value = existing ?? baselineFor(entity);
        next[entityId] = Array.from(new Set((value ?? []).map(String)));
      }
      return next;
    });
  };

  const handleUsersModalClose = () => {
    setShowUserModel(false);
    setShowBulkUserModel(false);
    setShowFolderPermissionModel(false);
    setIsSiteLevelFolderPermissoin(false);
  };

  const handleFolderPermissionModalClose = () => {
    if (isRefreshingFolderPermissions) return;
    setShowFolderPermissionModel(false);
    setIsSiteLevelFolderPermissoin(false);
    setShowFolderPermissionView(false);
  };

  const handleOpenBulkUsers = () => {
    const gridKeys = getSelectedEntityKeysFromGrid();
    const stateKeys = selectedEntityIds ?? [];
    const keysToUse = gridKeys.length && gridKeys.length >= stateKeys.length ? gridKeys : stateKeys;

    if ((keysToUse?.length ?? 0) <= 0) return;

    const keySet = new Set((keysToUse ?? []).map((k) => String(k)));
    const targets = (entities ?? []).filter((e: any) => keySet.has(String(e?.entityId)));
    if (!targets?.length) return;

    const map = buildBulkUsersMap(targets);
    setBulkEntityUsersMap(map);
    setBulkEntityUsersSnapshot(map);
    setBulkSelectedEntities(targets);
    setShowBulkUserModel(true);
  };

  const isBulkUsersDirty = useMemo(() => {
    const ids = Array.from(
      new Set<number>([
        ...Object.keys(bulkEntityUsersSnapshot).map(Number),
        ...Object.keys(bulkEntityUsersMap).map(Number),
      ])
    );

    for (let i = 0; i < ids.length; i++) {
      const entityId = ids[i];
      const a = bulkEntityUsersSnapshot[entityId] ?? [];
      const b = bulkEntityUsersMap[entityId] ?? [];
      if (a.length !== b.length) return true;

      const setA = new Set(a);
      for (const id of b) {
        if (!setA.has(id)) return true;
      }
    }

    return false;
  }, [bulkEntityUsersMap, bulkEntityUsersSnapshot]);

  const handleSaveChanges = async () => {
    openScheduleProcessModal(true);
  }

  const fetchScheduledCountsDisabledDates = async () => {
    try {
      const result = await customPermissionClientApi.getLegalEntityPermissionsScheduledCounts();
      const counts = result?.counts ?? [];
      setMaxUniqueHqSiteCount(result?.maxUniqueHqSiteCount);

      if (!Number.isFinite(result?.maxUniqueHqSiteCount as number)) {
        setScheduledCountsDisabledDates([]);
        return;
      }

      const limit = result!.maxUniqueHqSiteCount as number;

      const disabledDates = (counts ?? [])
        .filter((c: ScheduledHqSiteCountByDateDto) => (c?.uniqueHqSiteCount ?? 0) >= limit)
        .map((c: ScheduledHqSiteCountByDateDto) => (c?.date ?? "").trim())
        .filter((d: string) => d !== "");

      setScheduledCountsDisabledDates(disabledDates);
    } catch (error) {
      setScheduledCountsDisabledDates([]);
      setMaxUniqueHqSiteCount(undefined);
      showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
    }
  };

  const openScheduleProcessModal = (fromSaveButton: boolean) => {
    setIsModalFromSaveButton(fromSaveButton);
    setShowScheduleProcessModal(true);
    void fetchScheduledCountsDisabledDates();
  };

  const handleSaveChangesWithoutModal = async (scheduleStatus: string = "NOT SCHEDULED", scheduleDate?: any) => {
    setIsLoading(true);
    try {
      const payload = buildPostPayload(scheduleStatus, scheduleDate);
      await customPermissionClientApi.legalEntityPermissions(payload);
      await fetchLegalEntities();
    } catch (error) {
      showSnackbar({ message: SnackbarMessage.FAILED_MESSAGE, severity: "error" });
    } finally {
      setIsLoading(false);
    }
  }

  const handleUsersChanges = async () => {
    try {
      updateEntityUserList(selectedLegalEntities!.entityId, usersInEntitySelectorValues.map(u => String(u.userId)))
      updateEntityUserListFolderPermission(selectedLegalEntities!.entityId, usersInEntitySelectorValues.map(u => String(u.userId)))
      await revalidateEntityIsRestricted(selectedLegalEntities!.entityId);
      handleUsersModalClose();
    } catch (error) {
      showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
    }
  }

  const handleBulkUsersChanges = async () => {
    try {
      const targets = (bulkSelectedEntities ?? [])
        .map((e: any) => ({ ...e, entityId: Number(e?.entityId) }))
        .filter((e: any) => Number.isFinite(e?.entityId) && e.entityId !== 0);

      for (const entity of targets) {
        const entityId = Number((entity as any).entityId);
        const updatedUserIds = bulkEntityUsersMap[entityId] ?? [];
        updateEntityUserList(entityId, updatedUserIds);
        updateEntityUserListFolderPermission(entityId, updatedUserIds);
      }

      await revalidateAllEntitiesIsRestricted();

      handleUsersModalClose();
    } catch (error) {
      showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
    }
  };

  const handleFolderChanges = async () => {
    try {
      if (isSiteLevelFolderPermission) {
        updateSiteFolderPermission(folderTreeSelectorValues);
        await revalidateAllEntitiesIsRestricted();
      } else {
        updateEntityFolderTree(selectedLegalEntities!.entityId, folderTreeSelectorValues);
        await revalidateEntityIsRestricted(selectedLegalEntities!.entityId);
      }
      handleUsersModalClose();
      setTimeout(refreshEntitiesGrid, 0);
    } catch (error) {
      showSnackbar({ message: SnackbarMessage.FOLDER_PERMISSION_UPDATE_FAIL, severity: "error" });
    }
  }

  const handleFolderPermission = () => {
    loadFolderTree(folderPermissions);
    setIsSiteLevelFolderPermissoin(true);
    openFolderPermission(setShowFolderPermissionView, setShowFolderPermissionModel)
  }

  const handleCancel = () => {
    clearEntitySelection();
    if (isDirty) {
      setShowUnsavedChangesModal(true);
    }
  }

  const handleUnsavedChanges = () => {
    resetEntitiesToSnapshot();
    clearEntitySelection();
    setShowUnsavedChangesModal(false);
    setTimeout(refreshEntitiesGrid, 0);
  }

  const handleAttemptClose = () => {
    setShowUnsavedChangesModal(false);
  }

  const handleScheduleProcessClose = () => {
    setShowScheduleProcessModal(false);
    setShowCancelProcessModel(false);
    setIsModalFromSaveButton(false);
    setScheduleDate(null);
    setScheduledCountsDisabledDates([]);
    setMaxUniqueHqSiteCount(undefined);
  }

  const handleSaveWithoutScheduling = async () => {
    handleScheduleProcessClose();
    if (isModalFromSaveButton) {
      await handleSaveChangesWithoutModal("NOT SCHEDULED");
    }
  }

  const handleCancelProcess = async () => {
    setIsLoading(true);
    try {
      handleScheduleProcessClose();
      const updateRequest: UpdateScheduleRequest = {
        siteId: siteID,
        status: "CANCELLED"
      };
      await customPermissionClientApi.updateLegalEntityPermissionsSchedule(updateRequest);
      await fetchLegalEntities();
      // showSnackbar({ message: SnackbarMessage.CUSTOM_PERMISSIONS, severity: "success" });
    } catch (error) {
      showSnackbar({ message: SnackbarMessage.FAILED_MESSAGE, severity: "error" });
    } finally {
      setIsLoading(false);
    }
  }


  const handleScheduleProcess = async () => {
    if (scheduleDate) {
      handleScheduleProcessClose();
      setIsLoading(true);
      try {
        const formattedDate = formatScheduleDateForApi(scheduleDate);

        if (isModalFromSaveButton) {
          await handleSaveChangesWithoutModal("SCHEDULED", formattedDate);
        } else {
          const updateRequest: UpdateScheduleRequest = {
            siteId: siteID,
            scheduledDate: formattedDate,
            status: "SCHEDULED"
          };
          await customPermissionClientApi.updateLegalEntityPermissionsSchedule(updateRequest);
          await fetchLegalEntities();
          // showSnackbar({ message: SnackbarMessage.CUSTOM_PERMISSIONS, severity: "success" });
        }
      } catch (error) {
        showSnackbar({ message: SnackbarMessage.FAILED_MESSAGE, severity: "error" });
      } finally {
        setIsLoading(false);
      }
    }
  }

  const exportToExcel = async () => {
    try {
      await exportManageCustomPermissionsToExcel(
        entities as Entity[],
        users as UserPerSite[],
        siteID,
        snapshotEntities as Entity[]
      );
      showSnackbar({ message: SnackbarMessage.EXPORT_TO_EXCEL_SUCCESS, severity: "success" });
    } catch (error) {
      showSnackbar({ message: SnackbarMessage.EXPORT_TO_EXCEL_FAIL, severity: "error" });
    }
  };

  const buildPostPayload = (scheduleStatus: string = "NOT SCHEDULED", scheduleDate?: any): CustomPermissionPayload => {
    const dateToPersist = scheduleStatus === "SCHEDULED"
      ? formatScheduleDateForApi(scheduleDate)
      : scheduleStatus === "NOT SCHEDULED"
        ? formatScheduleDateForApi(new Date())
        : undefined;
    const payload: CustomPermissionPayload = {
      hqSiteId: siteID,
      scheduledDate: dateToPersist,
      scheduleStatus: scheduleStatus,
      entities: []
    };

    const currentEntities = entities;
    const allSiteUsers = users;
    const entitiesArray: EntityPermission[] = [];

    const allSiteUserIds = Array.from(
      new Set(
        allSiteUsers.map(user =>
          String(user.userId)
        )
      )
    );

    const collectFolderIdsFromTree = (tree: any[] | undefined | null, set: Set<number>) => {
      const stack = Array.isArray(tree) ? [...tree] : [];
      while (stack.length) {
        const node = stack.pop();
        if (!node) continue;
        const folderId = Number(node.folderId);
        if (Number.isFinite(folderId) && folderId > 0) set.add(folderId);
        if (Array.isArray(node.children) && node.children.length) {
          stack.push(...node.children);
        }
      }
    };

    const entityAssociatedFolderIds = new Set<number>();
    (currentEntities ?? []).forEach((entity: any) => {
      if (!entity || !entity.entityId || entity.entityId === 0) return;
      collectFolderIdsFromTree(entity.folderTree, entityAssociatedFolderIds);
    });

    const globalChangedSiteFolderIds = new Set<number>(
      computeChangedFolderIds(folderPermissions ?? [], folderPermissionSnapshot ?? [], allSiteUserIds)
    );

    const collectFolderDetailsRecursively = (
      node: any,
      parentEntityId: number,
      parentEntity: any,
      folderDetails: { [key: string]: { [key: string]: string } }
    ): void => {
      if (node?.isCountryGroup) return;
      if (!node.folderId) return;
      if (node.entityId === 0 && node.parentFolderId === 0) return;

      folderDetails[node.folderId] = {};

      if (parentEntityId === 0) {
        const folderPermission = folderPermissions[0]?.children?.find((fp: any) =>
          fp.entityId === 0 && fp.folderId === node.folderId
        );

        if (folderPermission && folderPermission.users && Array.isArray(folderPermission.users)) {
          folderPermission.users.forEach((userId: string) => {
            const user = allSiteUsers.find(u => u.userId.toString() === userId.toString());
            if (user) {
              folderDetails[node.folderId][userId] = user.userEmail || '';
            }
          });
        } else if (node.users && Array.isArray(node.users)) {
          node.users.forEach((userId: string) => {
            const user = allSiteUsers.find(u => u.userId.toString() === userId.toString());
            if (user) {
              folderDetails[node.folderId][userId] = user.userEmail || '';
            }
          });
        } else {
          allSiteUsers.forEach((user: any) => {
            folderDetails[node.folderId][user.userId] = user.userEmail || '';
          });
        }
      } else {
        if (node.users && Array.isArray(node.users)) {
          node.users.forEach((userId: string) => {
            const user = allSiteUsers.find(u => u.userId.toString() === userId.toString());
            if (user) {
              folderDetails[node.folderId][userId] = user.userEmail || '';
            }
          });
        } else if (parentEntity.isRestricted && parentEntity.userList && Array.isArray(parentEntity.userList)) {
          parentEntity.userList.forEach((userId: string) => {
            const user = allSiteUsers.find(u => u.userId.toString() === userId.toString());
            if (user) {
              folderDetails[node.folderId][userId] = user.userEmail || '';
            }
          });
        } else if (!parentEntity.isRestricted) {
          allSiteUsers.forEach((user: any) => {
            folderDetails[node.folderId][user.userId] = user.userEmail || '';
          });
        }
      }

      if (node.children && Array.isArray(node.children)) {
        node.children.forEach((child: any) => {
          collectFolderDetailsRecursively(child, parentEntityId, parentEntity, folderDetails);
        });
      }
    };

    const customFoldersFromPermissions: any[] = [];

    if (folderPermissions[0] && folderPermissions[0].children && Array.isArray(folderPermissions[0].children)) {
      const customFolders = folderPermissions[0].children.filter((folder: any) =>
        folder.entityId === 0 &&
        folder.folderId &&
        !folder.isCountryGroup &&
        !entityAssociatedFolderIds.has(Number(folder.folderId)) &&
        !(folder.entityId === 0 && folder.parentFolderId === 0)
      );
      customFoldersFromPermissions.push(...customFolders);
    }

    customFoldersFromPermissions.forEach((folder: any) => {
      if (folder.folderId && !(folder.entityId === 0 && folder.parentFolderId === 0)) {
        const folderDetails: { [key: string]: { [key: string]: string } } = {};

        const customEntity = {
          entityId: 0,
          isRestricted: folder.isRestricted || false,
          userList: []
        };

        collectFolderDetailsRecursively(folder, 0, customEntity, folderDetails);

        const subtreeFolderIds = Object.keys(folderDetails)
          .map((k) => Number(k))
          .filter((id) => Number.isFinite(id) && id > 0);

        const changedFolderIds = subtreeFolderIds.filter((id) => globalChangedSiteFolderIds.has(id));

        if (folder.isCountryGroup) {
          return;
        }

        const hasRestrictedFolder =
          Object.values(folderDetails).some(
            folderUsers =>
              allSiteUserIds.some(
                userId =>
                  !Object.prototype.hasOwnProperty.call(
                    folderUsers,
                    userId
                  )
              )
          );

        entitiesArray.push({
          entityId: 0,
          folderId: folder.folderId,
          isChanged: globalChangedSiteFolderIds.has(Number(folder.folderId)),
          changedFolderIds,
          restricted: folder.isRestricted || hasRestrictedFolder,
          userList: {},
          folderDetails: folderDetails
        });
      }
    });

    currentEntities.forEach((entity: any) => {
      if (entity.entityId && entity.entityId !== 0) {
        const userList: { [key: string]: string } = {};
        const folderDetails: { [key: string]: { [key: string]: string } } = {};

        const snap = (snapshotEntities ?? []).find((e: Entity) => e?.entityId === entity.entityId);
        const changedFolderIds = snap
          ? computeChangedFolderIds(entity.folderTree ?? [], snap.folderTree ?? [], allSiteUserIds)
          : [];

        const isChanged = isEntityDirty(entity as Entity);

        if (entity.userList && Array.isArray(entity.userList)) {
          entity.userList.forEach((userId: string) => {
            const user = allSiteUsers.find(u => u.userId.toString() === userId.toString());
            if (user) {
              userList[userId] = user.userEmail || '';
            }
          });
        }

        if (entity.folderTree && Array.isArray(entity.folderTree)) {
          entity.folderTree.forEach((rootFolder: any) => {
            collectFolderDetailsRecursively(rootFolder, entity.entityId, entity, folderDetails);
          });
        }

        if (Object.keys(folderDetails).length === 0) {
          const entityFolderPermissions = folderPermissions.filter((folder: any) =>
            folder.entityId === 0 && folder.folderId && !folder.isCountryGroup
          );

          entityFolderPermissions.forEach((folder: any) => {
            collectFolderDetailsRecursively(folder, entity.entityId, entity, folderDetails);
          });
        }

        entitiesArray.push({
          entityId: entity.entityId,
          folderId: 0,
          isChanged,
          changedFolderIds,
          restricted: entity.isRestricted || false,
          userList: userList,
          folderDetails: folderDetails
        });
      }
    });

    payload.entities = entitiesArray;
    return payload;
  };

  const cellRenderEntityName = (cellData: any) => {
    const entityName = cellData?.data?.entityName as any;
    return renderTruncatedTextCell(entityName);
  };

  const handleDataGridCellPrepared = (e: any) => {
    applyOverflowTooltipOnCellPrepared(e);
  };

  const handleUsersClick = async (entity: any) => {
    setSelectedLegalEntities(entity);
    fetchUsersForLegalEntity(entity)
    setShowUserModel(true);
  };

  const cellRenderUsers = (cellData: any) => {
    const users = cellData?.data?.numberOfUsers as any;
    const isRestricted = cellData?.data?.isRestricted as boolean;

    return (
      <div
        className="hyper-link-blue"
        onClick={() => handleUsersClick(cellData?.data)}
      >
        {!isRestricted ? `All` : formatUsersCount(users ?? 0)}
      </div>
    );
  };

  const cellRenderIsRestricted = (cellData: any) => {
    const isRestricted = cellData?.data?.isRestricted as boolean;
    return <input type="checkbox" readOnly checked={isRestricted} />;
  }

  const cellRenderForDate = (cellData: any) => {
    const rawDate = cellData?.value ?? cellData?.data?.dateModified;
    if (!rawDate) return <div />;

    return <div>{convertFormatDate(rawDate, FormatDate.DD)}</div>;
  };

  const cellRenderActions = (e: any) => {
    const rowData = e.data;
    const options = legalEntitiesActionOptions.map((opt) => ({
      ...opt,
      onClick: () => {
        if (opt.value === "folder-permissions") {
          if (!rowData.isRestricted) {
            let userIds = users.map((u: UserPerSite) => String(u.userId))
            updateEntityUserList(rowData?.entityId, userIds)
          }
          setSelectedLegalEntities(rowData);
          loadFolderTree(rowData?.folderTree)
          setShowFolderPermissionModel(true);
        } else if (opt.value === "edit-users") {
          handleUsersClick(rowData);
        }
      },
    }));

    return (
      <ActionIconButton
        icon={<MoreHorizIcon />}
        title="See Details"
        options={options}
        className="dx-datagrid mcp-action-menu-button"
      />
    );
  };

  const handleScheduleDate = (newValue: any) => {
    if (!newValue) {
      setScheduleDate(null);
      return;
    }

    const formatted = formatScheduleDateForApi(newValue);
    if (formatted && scheduledCountsDisabledDatesSet.has(formatted)) {
      setScheduleDate(null);
      return;
    }

    setScheduleDate(newValue);
  };

  const isScheduleDateDisabled = (date: any): boolean => {
    if (!date) return false;
    const formatted = formatScheduleDateForApi(date);
    if (!formatted) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);

    if (normalized < today) return false;
    return scheduledCountsDisabledDatesSet.has(formatted);
  };

  const annotateScheduleCalendarDisabledDates = () => {
    const calendarBodies = document.querySelectorAll('.dx-overlay-content .dx-calendar .dx-calendar-body');
    const root = calendarBodies.length
      ? (calendarBodies[calendarBodies.length - 1] as HTMLElement)
      : (document.querySelector('.dx-calendar .dx-calendar-body') as HTMLElement | null);

    if (!root) return;

    const cells = root.querySelectorAll('.dx-calendar-cell');
    cells.forEach((cell) => {
      cell.removeAttribute('title');

      let raw = cell.getAttribute('data-value') || cell.getAttribute('aria-label');
      let cellDate: Date | undefined;

      if (raw) {
        const num = Number(raw);
        if (!Number.isNaN(num) && num > 0) {
          cellDate = new Date(num);
        } else {
          const parsed = new Date(raw);
          if (!Number.isNaN(parsed.getTime())) cellDate = parsed;
        }
      }

      if (!cellDate) return;
      const formatted = formatScheduleDateForApi(cellDate);
      if (!formatted) return;

      if (scheduledCountsDisabledDatesSet.has(formatted)) {
        cell.setAttribute('title', 'Queue is Full');
      }
    });
  };

  const isSaveDisabled = () => {
    if (isButtonDisabled) return true;
    if (!isDirty) return true;
    if (isloading) return true;

    return false;
  };

  const openFolderPermission = (
    setShowFolderPermissionView: React.Dispatch<React.SetStateAction<boolean>>,
    setShowFolderPermissionModel: React.Dispatch<React.SetStateAction<boolean>>
  ): void => {
    (
      shouldUseStandaloneFolderPermissionsPage
        ? setShowFolderPermissionView
        : setShowFolderPermissionModel
    )(true);
  };

  const contextValue = {
    isCsFlow,
    folderPermissionChangedCount,
    showPublicationNotice,
    scheduleDetails,
    customPermissionBannerData,
    isloading,
    isButtonDisabled,
    selectedEntityIds,
    entities,
    dataGridRef,
    setSelectedEntityIds,
    cellRenderEntityName,
    cellRenderUsers,
    cellRenderIsRestricted,
    cellRenderStatus,
    cellRenderScheduledDate,
    cellRenderPublishedDate,
    cellRenderForDate,
    cellRenderActions,
    handleDataGridCellPrepared,
    handleOpenBulkUsers,
    handleFolderPermission,
    exportToExcel,
    handleCancel,
    handleSaveChanges,
    isSaveDisabled,
    showUserModel,
    handleUsersModalClose,
    handleUsersChanges,
    selectedLegalEntities,
    isUserlistDirty,
    showBulkUserModel,
    handleBulkUsersChanges,
    isBulkUsersDirty,
    bulkSelectedEntities,
    users,
    bulkEntityUsersMap,
    setBulkEntityUsersMap,
    handleBulkSelectedEntityIdsChange,
    showFolderPermissionModel,
    handleFolderPermissionModalClose,
    handleFolderChanges,
    isRefreshingFolderPermissions,
    isSiteLevelFolderPermission,
    isSiteFolderPermissionDirty,
    isFolderTreeDirty,
    siteID,
    setSelectedLegalEntities,
    refreshManageCustomPermissionsAndReopenFolderPermissions,
    showUnsavedChangesModal,
    handleUnsavedChanges,
    handleAttemptClose,
    showScheduleProcessModal,
    handleScheduleProcess,
    scheduleDate,
    handleScheduleProcessClose,
    isModalFromSaveButton,
    handleSaveWithoutScheduling,
    handleScheduleDate,
    isScheduleDateDisabled,
    annotateScheduleCalendarDisabledDates,
    showCancelProcessModel,
    handleCancelProcess,
    duplicateEntityFolderNames,
    showFolderPermissionView
  };

  return contextValue;
};
